import { NODE_TYPES } from '../../../constants';
import { buildConditionLabel } from '../conditions/conditionUtils';

const generateNodeId = () => `node_${crypto.randomUUID()}`;

/**
 * Converts React Flow nodes + edges into the backend rule API format.
 * The flow canvas stores nodes and edges separately; the backend expects
 * a structured rule with trigger, conditions, and actions.
 */
export const flowToRule = (nodes, edges, metadata = {}) => {
  const triggerNode = nodes.find(n => n.type === NODE_TYPES.TRIGGER);
  const conditionNodes = nodes.filter(n => n.type === NODE_TYPES.CONDITION);

  const trigger = triggerNode ? {
    type: triggerNode.data.triggerType || 'event',
    config: {
      eventName: triggerNode.data.eventName,
      cronExpression: (triggerNode.data.schedule || '').replace(/['"]/g, '').trim(),
      timezone: triggerNode.data.timezone,
      webhookPath: triggerNode.data.webhookPath,
      ...(triggerNode.data.webhookSecret ? { webhookSecret: triggerNode.data.webhookSecret } : {}),
    },
  } : { type: 'manual', config: {} };

  // Build conditionGroup from condition nodes.
  // Each condition node holds a full conditionGroup tree in its data.
  // Multiple condition nodes get merged into a top-level AND group.
  let conditionGroup = null;
  if (conditionNodes.length === 1) {
    conditionGroup = conditionNodes[0].data.conditionGroup || null;
  } else if (conditionNodes.length > 1) {
    conditionGroup = {
      logic: 'AND',
      conditions: [],
      groups: conditionNodes
        .map(n => n.data.conditionGroup)
        .filter(Boolean),
    };
  }

  const orderedActions = getOrderedActions(nodes, edges, triggerNode);
  const actions = orderedActions.map(({ node, branch }, index) => ({
    type: node.data.actionType || '',
    name: node.data.label || node.data.actionType || 'Untitled Action',
    config: node.data.config || {},
    order: index,
    onError: node.data.onError || 'stop',
    branch,
  }));

  return {
    name: metadata.name || 'Untitled Rule',
    description: metadata.description || '',
    tags: metadata.tags || [],
    trigger,
    conditionGroup,
    actions,
    settings: metadata.settings || {},
    canvas: {
      nodes: nodes.map(n => ({ id: n.id, type: n.type, position: n.position, data: n.data })),
      edges: edges.map(e => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle, targetHandle: e.targetHandle })),
    },
  };
};

/**
 * Walk the edge graph from the start node to collect action nodes in order.
 * Tracks which condition branch (true/false/null) each action is downstream of.
 */
const getOrderedActions = (nodes, edges, startNode) => {
  if (!startNode) return nodes.filter(n => n.type === NODE_TYPES.ACTION).map(n => ({ node: n, branch: null }));

  const visited = new Set();
  const ordered = [];

  const walk = (nodeId, currentBranch) => {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);

    const node = nodes.find(n => n.id === nodeId);
    if (node && node.type === NODE_TYPES.ACTION) {
      ordered.push({ node, branch: currentBranch });
    }

    const outgoing = edges.filter(e => e.source === nodeId);
    for (const edge of outgoing) {
      let branch = currentBranch;
      if (edge.sourceHandle === 'yes') branch = true;
      else if (edge.sourceHandle === 'no') branch = false;
      walk(edge.target, branch);
    }
  };

  walk(startNode.id, null);
  return ordered;
};

/**
 * Converts a backend rule into React Flow nodes + edges for the canvas.
 * If the rule has stored flowData (positions from a previous save), it uses
 * that directly. Otherwise it reconstructs a simple top-to-bottom layout.
 */
export const ruleToFlow = (rule) => {
  if (rule.canvas?.nodes?.length) {
    return {
      nodes: rule.canvas.nodes,
      edges: rule.canvas.edges || [],
    };
  }

  const nodes = [];
  const edges = [];
  let y = 50;
  const x = 300;
  const ySpacing = 150;

  const triggerId = generateNodeId();
  nodes.push({
    id: triggerId,
    type: NODE_TYPES.TRIGGER,
    position: { x, y },
    data: {
      triggerType: rule.trigger?.type || 'event',
      eventName: rule.trigger?.config?.eventName || '',
      schedule: rule.trigger?.config?.cronExpression || rule.trigger?.config?.schedule || '',
      webhookPath: rule.trigger?.config?.webhookPath || '',
      webhookSecret: '',
      hasExistingSecret: !!rule.trigger?.config?.hasWebhookSecret,
      label: rule.trigger?.config?.eventName
        || (rule.trigger?.config?.webhookPath ? `Webhook: /${rule.trigger.config.webhookPath}` : '')
        || rule.trigger?.type || 'Trigger',
    },
  });

  let lastNodeId = triggerId;
  y += ySpacing;

  if (rule.conditionGroup) {
    const condId = generateNodeId();
    nodes.push({
      id: condId,
      type: NODE_TYPES.CONDITION,
      position: { x, y },
      data: {
        conditionGroup: rule.conditionGroup,
        label: buildConditionLabel(rule.conditionGroup),
      },
    });
    edges.push({
      id: `edge_${lastNodeId}_${condId}`,
      source: lastNodeId,
      target: condId,
      type: 'flow',
    });
    lastNodeId = condId;
    y += ySpacing;
  }

  if (rule.actions?.length) {
    const condNodeId = nodes.find(n => n.type === NODE_TYPES.CONDITION)?.id;

    const buildBranch = (actions, startId, handle, xOffset) => {
      let prevId = startId;
      let branchY = y;
      for (const action of actions) {
        const actId = generateNodeId();
        nodes.push({
          id: actId,
          type: NODE_TYPES.ACTION,
          position: { x: x + xOffset, y: branchY },
          data: {
            actionType: action.type,
            config: action.config || {},
            onError: action.onError || 'stop',
            label: action.type,
          },
        });
        edges.push({
          id: `edge_${prevId}_${actId}`,
          source: prevId,
          target: actId,
          sourceHandle: prevId === startId ? handle : undefined,
          type: 'flow',
        });
        prevId = actId;
        branchY += ySpacing;
      }
      return branchY;
    };

    const trueActions = rule.actions.filter(a => a.branch !== false);
    const falseActions = rule.actions.filter(a => a.branch === false);
    const hasBothBranches = condNodeId && falseActions.length;

    const trueEndY = buildBranch(trueActions, lastNodeId, 'yes', hasBothBranches ? -100 : 0);
    const falseEndY = (hasBothBranches) ? buildBranch(falseActions, condNodeId, 'no', 250) : trueEndY;

    y = Math.max(trueEndY, falseEndY);
  }

  return { nodes, edges };
};
